import { Camera, ChevronDown, ChevronUp, GripVertical, ListChecks, Plus, X } from 'lucide-react';
import { useState, type DragEvent, type FormEvent } from 'react';

import { Button } from '@/components/ui/button';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { EmptyState, ErrorState } from '@/components/ui/empty-state';
import { Field } from '@/components/ui/field';
import {
  useCreateTask,
  useDeleteTask,
  usePropertyCategory,
  useReorderTasks,
  useUpdateTask,
} from '@/hooks/property-categories';
import { ApiError } from '@/lib/api';
import { useToast } from '@/lib/toast-context';
import { cn } from '@/lib/utils';
import type { PropertyTaskView } from '@shared/schemas/catalog';

/*
 * The checklist behind one property category.
 *
 * Reordering has two paths and both are real. Dragging is the one people reach
 * for; the up/down buttons are the one that works with a keyboard, on a phone,
 * and with a screen reader. design-system.md §9.6 — drag-and-drop alone is a
 * control a whole group of users cannot operate, and this list decides what a
 * cleaner is asked to photograph.
 *
 * Every reorder sends the whole list rather than a moved index, so it is
 * idempotent and a stale order is refused rather than half-applied.
 */

export function ChecklistEditor({
  categoryId,
  categoryName,
}: {
  categoryId: string;
  categoryName: string;
}) {
  const toast = useToast();
  const detail = usePropertyCategory(categoryId);

  const createTask = useCreateTask(categoryId);
  const reorder = useReorderTasks(categoryId);

  const [newName, setNewName] = useState('');
  const [newRequiresPhoto, setNewRequiresPhoto] = useState(true);
  const [draggingId, setDraggingId] = useState<string | null>(null);

  if (detail.isError) {
    return (
      <ErrorState
        title="That checklist could not be loaded"
        onRetry={() => {
          void detail.refetch();
        }}
      />
    );
  }

  if (detail.isPending) {
    return (
      <div aria-busy="true" className="space-y-2">
        <span className="sr-only">Loading the checklist for {categoryName}</span>
        {Array.from({ length: 3 }, (_, index) => (
          <div key={index} aria-hidden="true" className="h-11 animate-pulse rounded-sm bg-white" />
        ))}
      </div>
    );
  }

  const tasks = detail.data.tasks;

  function move(taskId: string, direction: -1 | 1) {
    const order = tasks.map((task) => task.id);
    const from = order.indexOf(taskId);
    const to = from + direction;
    if (from < 0 || to < 0 || to >= order.length) return;

    const next = [...order];
    next.splice(to, 0, ...next.splice(from, 1));
    submitOrder(next);
  }

  function submitOrder(taskIds: string[]) {
    reorder.mutate(taskIds, {
      onError: (cause) => {
        toast.error(
          cause instanceof ApiError ? cause.message : 'That order could not be saved. Try again.',
        );
      },
    });
  }

  function onDrop(event: DragEvent<HTMLLIElement>, targetId: string) {
    event.preventDefault();
    setDraggingId(null);
    if (!draggingId || draggingId === targetId) return;

    const order = tasks.map((task) => task.id);
    const from = order.indexOf(draggingId);
    const to = order.indexOf(targetId);
    if (from < 0 || to < 0) return;

    const next = [...order];
    next.splice(to, 0, ...next.splice(from, 1));
    submitOrder(next);
  }

  function addTask(event: FormEvent) {
    event.preventDefault();
    if (newName.trim().length === 0) return;

    createTask.mutate(
      { name: newName, requiresPhoto: newRequiresPhoto },
      {
        onSuccess: (task) => {
          toast.success(`Added “${task.name}”.`);
          setNewName('');
          setNewRequiresPhoto(true);
        },
        onError: (cause) => {
          toast.error(cause instanceof ApiError ? cause.message : 'That task could not be added.');
        },
      },
    );
  }

  return (
    <div>
      {tasks.length === 0 ? (
        <EmptyState
          icon={ListChecks}
          title="No tasks on this checklist yet."
          hint={`Nothing has been written down for ${categoryName}. Add the tasks a cleaner should work through.`}
        />
      ) : (
        <ol className="space-y-2" aria-label={`Checklist for ${categoryName}`}>
          {tasks.map((task, index) => (
            <TaskRow
              key={task.id}
              task={task}
              index={index}
              total={tasks.length}
              categoryId={categoryId}
              isDragging={draggingId === task.id}
              isReordering={reorder.isPending}
              onDragStart={() => {
                setDraggingId(task.id);
              }}
              onDragEnd={() => {
                setDraggingId(null);
              }}
              onDrop={(event) => {
                onDrop(event, task.id);
              }}
              onMove={(direction) => {
                move(task.id, direction);
              }}
            />
          ))}
        </ol>
      )}

      <form onSubmit={addTask} className="mt-3 rounded-sm border border-ink-300 bg-white p-3">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
          <Field
            label="New task"
            name="taskName"
            value={newName}
            className="sm:min-w-0"
            onChange={(event) => {
              setNewName(event.target.value);
            }}
            placeholder="Vacuum all carpeted areas and rugs"
          />
          <Button type="submit" disabled={createTask.isPending || newName.trim().length === 0}>
            <Plus aria-hidden="true" />
            {createTask.isPending ? 'Adding…' : 'Add task'}
          </Button>
        </div>

        <label className="mt-2 flex min-h-touch items-center gap-2.5">
          <input
            type="checkbox"
            name="requiresPhoto"
            checked={newRequiresPhoto}
            onChange={(event) => {
              setNewRequiresPhoto(event.target.checked);
            }}
            className="size-4 shrink-0 accent-honey-500"
          />
          <span className="text-meta text-ink-700">
            Requires a photo — the cleaner completes this task by uploading one
          </span>
        </label>
      </form>
    </div>
  );
}

function TaskRow({
  task,
  index,
  total,
  categoryId,
  isDragging,
  isReordering,
  onDragStart,
  onDragEnd,
  onDrop,
  onMove,
}: {
  task: PropertyTaskView;
  index: number;
  total: number;
  categoryId: string;
  isDragging: boolean;
  isReordering: boolean;
  onDragStart: () => void;
  onDragEnd: () => void;
  onDrop: (event: DragEvent<HTMLLIElement>) => void;
  onMove: (direction: -1 | 1) => void;
}) {
  const toast = useToast();
  const update = useUpdateTask(categoryId);
  const remove = useDeleteTask(categoryId);
  const [confirming, setConfirming] = useState(false);

  function togglePhoto() {
    update.mutate(
      {
        taskId: task.id,
        input: { name: task.name, isRequired: task.isRequired, requiresPhoto: !task.requiresPhoto },
      },
      {
        onError: (cause) => {
          toast.error(cause instanceof ApiError ? cause.message : 'That could not be saved.');
        },
      },
    );
  }

  return (
    <li
      draggable
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onDragOver={(event) => {
        event.preventDefault();
      }}
      onDrop={onDrop}
      className={cn(
        // Stacked below 640px, one line above it. On a narrow screen the task
        // name and the controls cannot share a row: `flex-1 min-w-0` shrinks
        // the name to nothing and the badge, which cannot wrap, then sits on
        // top of it. That was visible at 375px and unreadable.
        'flex flex-col gap-2 rounded-sm border border-ink-300 bg-white p-2 sm:flex-row sm:items-center',
        isDragging && 'opacity-50',
        isReordering && 'opacity-70',
      )}
    >
      <div className="flex min-w-0 flex-1 items-center gap-2">
        <GripVertical aria-hidden="true" className="size-4 shrink-0 cursor-grab text-ink-500" />
        <span className="w-6 shrink-0 text-center text-meta text-ink-500">{index + 1}</span>
        <span className="min-w-0 text-body text-ink-900">{task.name}</span>
      </div>

      <div className="flex shrink-0 items-center justify-end gap-1">
        {/*
        A toggle, not a static badge. `requires_photo` is the single most
        consequential field on this screen — it decides whether a cleaner can
        tick a task off without evidence — so it is one press away and its state
        is readable without hovering.
      */}
        <button
          type="button"
          onClick={togglePhoto}
          disabled={update.isPending}
          aria-pressed={task.requiresPhoto}
          className={cn(
            'inline-flex min-h-touch shrink-0 items-center gap-1.5 rounded-full px-2.5 text-meta font-medium',
            task.requiresPhoto
              ? 'bg-status-completed/15 text-status-completed-text'
              : 'bg-ink-100 text-ink-700',
          )}
        >
          <Camera aria-hidden="true" className="size-3.5" />
          {task.requiresPhoto ? 'Photo required' : 'No photo'}
        </button>

        {/*
          The keyboard path to reordering. Dragging is the discoverable one and
          this is the one that works for everybody else.
        */}
        <button
          type="button"
          aria-label={`Move “${task.name}” up`}
          disabled={index === 0 || isReordering}
          onClick={() => {
            onMove(-1);
          }}
          className="grid min-h-touch min-w-touch place-items-center text-ink-500 hover:text-ink-900 disabled:opacity-30"
        >
          <ChevronUp aria-hidden="true" className="size-4" />
        </button>
        <button
          type="button"
          aria-label={`Move “${task.name}” down`}
          disabled={index === total - 1 || isReordering}
          onClick={() => {
            onMove(1);
          }}
          className="grid min-h-touch min-w-touch place-items-center text-ink-500 hover:text-ink-900 disabled:opacity-30"
        >
          <ChevronDown aria-hidden="true" className="size-4" />
        </button>
        <button
          type="button"
          aria-label={`Remove “${task.name}”`}
          onClick={() => {
            setConfirming(true);
          }}
          className="grid min-h-touch min-w-touch place-items-center text-ink-500 hover:text-status-priority-text"
        >
          <X aria-hidden="true" className="size-4" />
        </button>
      </div>

      <ConfirmDialog
        open={confirming}
        onOpenChange={setConfirming}
        title="Remove this task?"
        description={
          <>
            “{task.name}” comes off this checklist, so future jobs will not ask for it. Jobs already
            created keep their own copy.
          </>
        }
        confirmLabel="Remove the task"
        destructive
        isPending={remove.isPending}
        onConfirm={() => {
          remove.mutate(task.id, {
            onSuccess: () => {
              toast.success(`Removed “${task.name}”.`);
              setConfirming(false);
            },
            onError: (cause) => {
              toast.error(cause instanceof ApiError ? cause.message : 'That could not be removed.');
            },
          });
        }}
      />
    </li>
  );
}
